import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { BookingDataService } from '../../../services/booking-data/booking-data.service';
import { HttpClient } from '@angular/common/http';
import {
  catchError,
  EMPTY,
  first,
  switchMap,
  throwError,
  takeUntil,
  Subject,
} from 'rxjs';
import { BookingPdfHtmlComponent } from './booking-pdf-html.component';
import { SimpleLoadingModalComponent } from '../../modals/simple-loading-modal.component';
import { AuthService } from '../../../services/auth/auth.service';
import { BookingService } from '../../../services/resources/booking.service';
import {
  Booking,
  EmailInterface,
  newGuideInterface,
  VoucherInterface,
} from '../../../core/models';
import { TimestampToDatePipe } from '../../../core/timestamp-to-date.pipe';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { extractHashFromFilename } from '../../../core/booking-from.utils';
import { Timestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { FileStatusComponent } from './file-status.component';
import { EmailPreviewComponent } from '../../modals/email-preview.component';
import { SimpleConfirmModalComponent } from '../../modals/simple-confirm-modal.component';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-booking-pdf-generator',
  standalone: true,
  template: `
    <div
      class="flex flex-row justify-around mx-auto max-w-7xl pt-6 px-4 sm:px-6 lg:px-8"
    >
      <button
        (click)="goBackToDashboard()"
        type="button"
        class=" rounded-md disabled:opacity-25 bg-columbus-blue-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-columbus-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
      >
        <div class="flex w-full items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="size-6"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3"
            />
          </svg>
        </div>
      </button>
      <div class="flex flex-row">
        <ng-container
          *ngIf="
            this.loadedBooking && !this.loadedBooking.pdfUrl;
            else updatePDF
          "
        >
          <button
            (click)="generatePDF()"
            type="button"
            [disabled]="!loadedBooking"
            class="rounded-md disabled:opacity-25 bg-columbus-blue-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-columbus-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
          >
            Generate PDF
          </button>
        </ng-container>
        <ng-template #updatePDF>
          <button
            (click)="generatePDF()"
            type="button"
            [disabled]="!loadedBooking"
            class="rounded-md disabled:opacity-25 bg-columbus-blue-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-columbus-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
          >
            Refresh PDF
          </button>
        </ng-template>
        <app-file-status
          [svgLetter]="'B'"
          [hash]="loadedBooking?.hash"
          [url]="loadedBooking?.pdfUrl"
        />
      </div>
      <div class="flex flex-row">
        <ng-container
          *ngIf="
            this.loadedBooking &&
              (!loadedBooking.voucher || !loadedBooking.voucher.voucherUrl);
            else updateVoucher
          "
        >
          <button
            (click)="generateVoucher()"
            type="button"
            [disabled]="!loadedBooking"
            class="rounded-md disabled:opacity-25 bg-columbus-blue-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-columbus-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
          >
            Generate Voucher
          </button>
        </ng-container>
        <ng-template #updateVoucher>
          <button
            (click)="generateVoucher()"
            type="button"
            [disabled]="!loadedBooking"
            class="rounded-md disabled:opacity-25 bg-columbus-blue-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-columbus-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
          >
            Refresh Voucher
          </button>
        </ng-template>

        <app-file-status
          [svgLetter]="'V'"
          [hash]="loadedBooking?.hash"
          [url]="loadedBooking?.voucher?.voucherUrl"
        />
      </div>
      <button
        (click)="toggleEmailPreviewModal()"
        type="button"
        [disabled]="!loadedBooking || !loadedBooking.pdfUrl"
        class=" rounded-md disabled:opacity-25 bg-columbus-blue-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-columbus-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
      >
        Booking Email
      </button>
    </div>

    <div *ngIf="loadedBooking">
      <app-booking-pdf-html [booking]="loadedBooking" />
    </div>
    <!-- hidden (not destroyed) while the preview modal covers them: a PDF iframe
         is expensive to lay out and repaint, and remounting would refetch it -->
    <div
      *ngIf="loadedBooking?.pdfUrl || loadedBooking?.voucher?.voucherUrl"
      [class.hidden]="emailPreviewToggler"
      class="flex flex-col xl:flex-row justify-around items-center"
    >
      <div
        *ngIf="loadedBooking?.pdfUrl"
        class="flex-none flex w-[210mm] max-w-full justify-center my-8"
      >
        <iframe
          [src]="sanitizedPdfUrl"
          frameborder="0"
          loading="lazy"
          class="block mx-auto w-full h-full aspect-[210/305] max-w-[210mm] border-0 shadow-md"
        ></iframe>
      </div>

      <!-- only render this when voucherUrl is actually there -->
      <div
        *ngIf="loadedBooking?.voucher?.voucherUrl"
        class="flex-none flex w-[105mm] max-w-full justify-center my-8"
      >
        <iframe
          [src]="sanitizedVoucherUrl"
          loading="lazy"
          class="block mx-auto w-full h-full max-w-[105mm] aspect-[105/157] border-0 shadow-md"
          frameborder="0"
        ></iframe>
      </div>
    </div>

    <div class="p-4 bg-gray-100 rounded-lg shadow">
      <h3 class="text-lg font-semibold mb-2">Last Changes</h3>
      <ng-container
        *ngIf="
          loadedBooking &&
            loadedBooking.changes &&
            loadedBooking.changes.length > 0;
          else noChanges
        "
      >
        <ul role="list" class="space-y-6">
          <li
            *ngFor="let change of loadedBooking.changes"
            class="relative flex gap-x-4"
          >
            <div
              class="absolute top-0 -bottom-6 left-0 flex w-6 justify-center"
            >
              <div class="w-px bg-gray-200"></div>
            </div>
            <div
              class="relative flex size-6 flex-none items-center justify-center bg-white"
            >
              <div
                class="size-1.5 rounded-full bg-gray-100 ring-1 ring-gray-300"
              ></div>
            </div>
            <div class="flex-auto">
              <p class="py-0.5 text-xs/5 text-gray-500">
                <span class="font-medium text-gray-900">{{
                  change.changeAuthor.name
                }}</span>
                made a change.
              </p>
              <time class="flex-none py-0.5 text-xs/5 text-gray-500">
                {{
                  change.timestamp
                    | timestampToDate
                    | date: 'HH:mm:ss - dd.MM.YYYY'
                }}
              </time>
              <p class="text-xs/5 text-gray-600 italic">
                Change Type:
                <span class="font-semibold">{{ change.changeType }}</span>
              </p>
              <ul class="list-disc list-inside text-xs/5 text-gray-700 mt-1">
                <li *ngFor="let key of getChangedKeys(change.changes)">
                  <strong class="text-gray-800">{{ key }}</strong>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      </ng-container>
      <ng-template #noChanges>
        <p class="text-gray-500 italic">No changes recorded.</p>
      </ng-template>
    </div>

    <div *ngIf="pdfGenToggler">
      <app-simple-loading-modal [title]="'Generating PDF...'" />
    </div>
    <div *ngIf="emailPreviewToggler && loadedBooking">
      <app-email-preview
        [emailBooking]="loadedBooking"
        [sending]="emailSenderToggler"
        (close)="onCloseEmailPreview($event)"
        (sendEmail)="onSendEmail($event)"
      />
    </div>
    <div *ngIf="emailSentCheckToggler">
      <app-simple-confirm-modal
        (close)="toggleEmailSentCheckModal($event)"
        [title]="'Email sent successfully!'"
        [confirmButtonText]="'OK'"
      />
    </div>
  `,
  styles: ``,
  imports: [
    CommonModule,
    BookingPdfHtmlComponent,
    SimpleLoadingModalComponent,
    SimpleConfirmModalComponent,
    TimestampToDatePipe,
    FileStatusComponent,
    EmailPreviewComponent,
  ],
})
export class BookingPdfGeneratorComponent implements OnInit, OnDestroy {
  bookingDataService = inject(BookingDataService);
  bookingService = inject(BookingService);
  http = inject(HttpClient);
  authService = inject(AuthService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  sanitizer = inject(DomSanitizer);

  pdfEndpoint = environment.functionsLinks.pdfEndpoint;
  voucherEndpoint = environment.functionsLinks.voucherEndpoint;
  emailEndpoint = environment.functionsLinks.emailEndpoint;
  //pdfEndpoint ='http://127.0.0.1:5001/cadmus-dev-6ce41/europe-west3/generatePDF';
  //voucherEndpoint = 'http://127.0.0.1:5001/cadmus-dev-6ce41/europe-west3/generateVoucher';
  //emailEndpoint = 'http://127.0.0.1:5001/cadmus-dev-6ce41/europe-west3/sendEmail';

  loadedBooking: Booking | null = null;
  pdfGenToggler = false;
  emailSenderToggler = false;
  emailPreviewToggler = false;
  emailSentCheckToggler = false;
  calendarWeek: string | null = null;
  sanitizedPdfUrl?: SafeResourceUrl;
  sanitizedVoucherUrl?: SafeResourceUrl;

  private destroy$ = new Subject<void>();
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateSanitizedUrls(booking: Booking): void {
    this.sanitizedPdfUrl = booking.pdfUrl
      ? this.sanitizer.bypassSecurityTrustResourceUrl(booking.pdfUrl)
      : undefined;

    this.sanitizedVoucherUrl = booking.voucher?.voucherUrl
      ? this.sanitizer.bypassSecurityTrustResourceUrl(
          booking.voucher.voucherUrl,
        )
      : undefined;
  }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        switchMap((params: ParamMap) => {
          const week = params.get('calendarWeek');
          const shipId = params.get('shipId');
          const bookingId = params.get('bookingId');
          if (!week || !shipId || !bookingId) {
            return EMPTY;
          }
          // any time the params change, cancel the old booking request and start a new one
          return this.bookingService.getBookingData(week, shipId, bookingId);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((booking) => {
        this.loadedBooking = booking;
        // console.log('Log', this.loadedBooking);
        if (booking) {
          this.updateSanitizedUrls(booking);
        }
      });

    if (!this.loadedBooking) {
      this.loadedBooking = this.bookingDataService.booking();

      //console.log('Log', this.loadedBooking);
      if (this.loadedBooking && this.loadedBooking.pdfUrl) {
        this.sanitizedPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          this.loadedBooking.pdfUrl,
        );
      }
      if (this.loadedBooking && this.loadedBooking.voucher?.voucherUrl) {
        this.sanitizedVoucherUrl =
          this.sanitizer.bypassSecurityTrustResourceUrl(
            this.loadedBooking.voucher.voucherUrl,
          );
      }
    }
  }

  goBackToDashboard() {
    this.router.navigate([`dashboard`]);
  }

  togglePDFGenModal() {
    this.pdfGenToggler = !this.pdfGenToggler;
  }

  toggleEmailPreviewModal() {
    this.emailPreviewToggler = !this.emailPreviewToggler;
  }

  toggleEmailSentCheckModal(value?: boolean) {
    this.emailSentCheckToggler = !this.emailSentCheckToggler;
  }

  onCloseEmailPreview(value: boolean) {
    this.toggleEmailPreviewModal();
  }

  generatePDF() {
    if (this.loadedBooking) {
      const args = {
        booking: this.loadedBooking,
      };

      if (args.booking) {
        this.togglePDFGenModal();
        this.http
          .post(this.pdfEndpoint, args)
          .pipe(
            first(),
            catchError((error: Error) => {
              console.error('Error generating PDF:', error);
              this.handleError(error); // Custom error handler
              this.togglePDFGenModal(); // Ensure loading spinner is stopped
              return throwError(() => error); // Re-throw the error if needed
            }),
          )
          .subscribe({
            next: (response: any) => {
              if (args.booking && response.url) {
                if (
                  typeof response.url === 'string' &&
                  response.url.startsWith('http')
                ) {
                  args.booking.pdfUrl = response.url;
                }

                //                console.log('new Booking', args.booking);
                this.bookingService.updateBookingChanges(args.booking);
              }
              this.updateSanitizedUrls(args.booking);
              this.togglePDFGenModal();
            },
            error: (err) => {
              console.error('Subscription Error:', err);
            },
          });
      }
    } else {
      console.log('ERROR - NO BOOKING');
    }
  }

  generateVoucher() {
    if (this.loadedBooking) {
      let guideAmount = 0;
      let tourList: Record<string, number> = {};
      if (this.loadedBooking.guides) {
        guideAmount = Object.values(this.loadedBooking.guides ?? {}).filter(
          (g) => !g.storno.isActive,
        ).length;

        tourList = this.createTourList(this.loadedBooking.guides);
      }

      const voucher: VoucherInterface = {
        id: uuidv4().replace(/-/g, '').substring(0, 20),
        bookingHash: this.loadedBooking.hash,
        ship: this.loadedBooking.ship,
        duration: this.loadedBooking.duration,
        createdDate: Timestamp.fromDate(new Date()),
        guideAmount: guideAmount,
        tourList: tourList,
        cruiseDetails: this.loadedBooking.cruiseDetails,
        busInfos: this.loadedBooking.busInfos,
        addonText: this.loadedBooking.voucherAddonText || '',
      };

      this.loadedBooking.voucher = voucher;

      const args = {
        voucher: voucher,
      };

      if (args.voucher) {
        this.togglePDFGenModal();
        this.http
          .post(this.voucherEndpoint, args)
          .pipe(
            first(),
            catchError((error: Error) => {
              console.error('Error generating PDF:', error);
              this.handleError(error); // Custom error handler
              this.togglePDFGenModal(); // Ensure loading spinner is stopped
              return throwError(() => error); // Re-throw the error if needed
            }),
          )
          .subscribe({
            next: (response: any) => {
              if (args.voucher && response.url) {
                args.voucher.voucherUrl = response.url;
                if (this.loadedBooking) {
                  this.loadedBooking.voucher = args.voucher;

                  this.bookingService.updateBookingChanges(this.loadedBooking);

                  this.updateSanitizedUrls(this.loadedBooking);
                }
              }

              this.togglePDFGenModal();
            },
            error: (err) => {
              console.error('Subscription Error:', err);
            },
          });
      }
    } else {
      console.log('ERROR - NO VOUCHER');
    }
  }

  createTourList(guides: Record<string, newGuideInterface>) {
    let tourList: Record<string, number> = {};

    Object.entries(guides).forEach(([id, guide]) => {
      if (!guide.storno.isActive) {
        const tourName = guide.tour.tourInfo.tourName || 'TBA';
        tourList[tourName] = (tourList[tourName] || 0) + 1;
      }
    });

    return tourList;
  }

  handleError(error: Error, action = 'generate PDF') {
    alert(
      `Failed to ${action}. Please try again later. Error: ` + error.message,
    );
  }

  onSendEmail(emailData: EmailInterface) {
    if (!emailData) {
      console.error('No Email Data');
      return;
    }

    // Set, never toggle: the user may close the preview mid-request, and a toggle
    // in the response handler would pop it back open.
    this.emailSenderToggler = true;

    this.http
      .post<{ message: string; response: Response }>(
        this.emailEndpoint,
        emailData,
      )
      .pipe(
        first(),
        catchError((error: Error) => {
          console.error('Error sending email:', error);
          // Leave the preview open so the user can just press Senden again.
          this.emailSenderToggler = false;
          this.handleError(error, 'send the e-mail');
          return throwError(() => error); // Re-throw the error if needed
        }),
      )
      .subscribe({
        next: (serverResponse) => {
          console.log('Response from server:', serverResponse);

          if (serverResponse.response) {
            console.log('Email sent successfully');
          } else {
            console.warn(
              'Email request processed, but response indicates failure',
              serverResponse,
            );
          }

          this.emailSenderToggler = false;
          this.emailPreviewToggler = false;
          this.toggleEmailSentCheckModal();
        },
        error: (err) => {
          console.error('Subscription Error:', err);
        },
      });
  }

  getChangedKeys(changes: Partial<Booking>): (keyof Booking)[] {
    return Object.keys(changes) as (keyof Booking)[];
  }

  checkHashForUrl(hash: string, url: string): boolean {
    if (hash === extractHashFromFilename(url)) {
      return true;
    } else {
      return false;
    }
  }
}
